import React, { useMemo, useState } from "react";
import { Clock, Zap, Languages, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

export default function SummaryView({ summary }) {
  const { t, lang } = useLang();
  const [translated, setTranslated] = useState(null); // null = show original
  const [target, setTarget] = useState("vi");
  const [busy, setBusy] = useState(false);

  const original = useMemo(() => ({
    main_idea: summary?.main_idea || "",
    key_findings: summary?.key_findings || [],
    conclusion: summary?.conclusion || "",
    full_summary: summary?.full_summary || "",
    source_title: summary?.source_title || "",
  }), [summary]);

  if (!summary) return null;
  const view = translated || original;

  const doTranslate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const nextTarget = translated ? null : (lang === "vi" ? "vi" : "vi"); // toggle
      if (translated) { setTranslated(null); setBusy(false); return; }
      const t1 = "vi";
      setTarget(t1);
      // Send a single payload with delimiters so we can split back
      const marker = "\n<<<>>>\n";
      const payload = [
        original.source_title,
        original.main_idea,
        original.conclusion,
        (original.key_findings || []).join("\n---FIND---\n"),
        original.full_summary,
      ].join(marker);
      const { data } = await api.post("/translate", { text: payload, target: t1 });
      const parts = (data.text || "").split(marker);
      const [tit, main, conc, findings, full] = [parts[0] || original.source_title, parts[1] || "", parts[2] || "", parts[3] || "", parts[4] || ""];
      setTranslated({
        source_title: tit.trim(),
        main_idea: main.trim(),
        conclusion: conc.trim(),
        key_findings: findings.split("\n---FIND---\n").map((s) => s.trim()).filter(Boolean),
        full_summary: full.trim(),
      });
    } catch (e) {
      toast.error(lang === "vi" ? "Không thể dịch. Thử lại nhé." : "Translation failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article data-testid="summary-view" className="rounded-3xl border border-border bg-card p-8 md:p-10 decoding">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-3">
            {summary.mode?.toUpperCase()} · Readlet summary{summary.model_label ? ` · ${summary.model_label}` : ""}{translated ? ` · ${target.toUpperCase()}` : ""}
          </p>
          <h2 data-testid="summary-title" className="text-3xl sm:text-4xl leading-tight tracking-tight">
            {view.source_title}
          </h2>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            data-testid="translate-btn"
            onClick={doTranslate}
            disabled={busy}
            className="h-9 rounded-full border border-border px-3.5 text-sm flex items-center gap-2 hover:border-primary/60 hover:bg-accent transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Languages size={14} />}
            <span>
              {busy
                ? t("summary.translating")
                : translated
                  ? t("summary.translate.to.en")
                  : t("summary.translate.to.vi")}
            </span>
          </button>
          <div className="text-right">
            <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1">Reading</p>
            <p className="text-lg font-display flex items-center gap-1.5">
              <Clock size={14} className="text-muted-foreground" /> {summary.reading_time_min} min
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] tracking-[0.2em] uppercase text-primary font-semibold mb-1">Summary</p>
            <p className="text-lg font-display flex items-center gap-1.5 text-primary">
              <Zap size={14} /> {summary.summary_time_min} min
            </p>
          </div>
        </div>
      </div>

      {/* Main idea */}
      <section className="mb-8">
        <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-3">{t("summary.main")}</p>
        <p data-testid="summary-main-idea" className="text-2xl leading-snug font-display">{view.main_idea}</p>
      </section>

      {/* Findings */}
      <section className="mb-8">
        <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-4">{t("summary.findings")}</p>
        <ul className="space-y-4" data-testid="summary-findings">
          {(view.key_findings || []).map((f, i) => (
            <li key={i} className="flex gap-4">
              <span className="font-display text-primary text-lg leading-none pt-1 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-[17px] leading-relaxed">{f}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Conclusion */}
      <section className="mb-8">
        <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-3">{t("summary.conclusion")}</p>
        <p data-testid="summary-conclusion" className="text-lg leading-relaxed">{view.conclusion}</p>
      </section>

      {/* Full summary */}
      {view.full_summary && (
        <section className="pt-6 border-t border-border">
          <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-4">{t("summary.full")}</p>
          <div className="prose-lumina max-w-3xl" data-testid="summary-full">
            {view.full_summary.split(/\n\n+/).map((para, idx) => (
              <p key={idx}>{para}</p>
            ))}
          </div>
        </section>
      )}

      {/* Meeting-mode transcript, if present */}
      {summary.transcript && (
        <section className="pt-6 mt-6 border-t border-border">
          <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-4">{t("meeting.transcript")}</p>
          <div className="rounded-2xl bg-muted p-4 text-sm leading-relaxed max-h-72 overflow-auto whitespace-pre-wrap" data-testid="meeting-transcript">
            {summary.transcript}
          </div>
        </section>
      )}
    </article>
  );
}
